import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { cn } from '@seen/ui'
import { type JSX, lazy, Suspense, useEffect } from 'react'
import { useParams } from 'react-router'
import { Loader } from '#src/components/core/Loader.tsx'
import { useDeskView } from '#src/hooks/useDeskViewStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useSearchFocusRequestStore } from '#src/hooks/useSearchFocusRequestStore.ts'
import { DESK_VIEW_GROUP_CLASSNAME } from '#src/pages/desks/deskViewLayout.ts'
import { DeskViewToggle } from '#src/pages/desks/DeskViewToggle.tsx'

// Lazy-load the desk canvas so @xyflow/react (~117 KB raw / ~36 KB gzipped) and
// its CSS — reachable in the app only through this page's flow tree — ship in
// their own chunk instead of the portal's initial bundle. The single-desk view
// is always reached by navigation, never above the fold, so the Suspense gap is
// covered by the same <Loader /> the other lazy routes use.
const DeskPageImpl = lazy(() =>
  import('#src/pages/desks/DeskPageImpl.tsx').then((m) => ({
    default: m.DeskPageImpl,
  }))
)

// Lazy for the same reason as the canvas: only one of the two views is ever
// mounted, so neither belongs in the initial bundle.
const DeskListView = lazy(() =>
  import('#src/pages/desks/DeskListView.tsx').then((m) => ({
    default: m.DeskListView,
  }))
)

export function DeskPage(): JSX.Element {
  const { deskId } = useParams()
  invariant(deskId, 'deskId is required')
  /* Flag values arrive synchronously from FeatureFlagContext — the portal
   * resolves them once and provides a complete snapshot, falling back to each
   * descriptor's default. There is no unresolved window, so this needs no
   * loading guard. */
  const isViewSwitcherEnabled = useFeatureFlag(featureFlags.deskListView)
  const view = useDeskView(deskId)

  /* A held search-focus request belongs to the desk that published it, and
   * nothing else bounds its life: it is released when a field focuses, and the
   * field is behind a lazy chunk and a room load, so leaving during that window
   * strands it. The next desk opened in list view would then consume it and take
   * the caret unasked — the request names a combo, not a desk, because the field
   * consuming it is the shared search input and knows nothing about desks.
   *
   * Keyed on `deskId` rather than plain unmount, because this page is the route
   * element for every desk: navigating from one desk to another changes the
   * param without unmounting, which is the case that would carry a request
   * across. The switch this exists for happens inside one deskId, so it is never
   * the thing being cleared. */
  const clearSearchFocusRequest = useSearchFocusRequestStore(
    (state) => state.clearSearchFocusRequest
  )
  useEffect(
    () => () => clearSearchFocusRequest(),
    [deskId, clearSearchFocusRequest]
  )

  /* Flag off: exactly today's tree, wrapper and all. The wrapper div below is
   * not cosmetic to the canvas — it establishes a containing block, which
   * moves DeskPageImpl's absolutely-positioned "sync disconnected" banner —
   * so it belongs behind the flag with everything else. */
  if (!isViewSwitcherEnabled) {
    return (
      <Suspense fallback={<Loader />}>
        <DeskPageImpl />
      </Suspense>
    )
  }

  return (
    /* The group is the scope the switcher band's `:has()` reads: the canvas's
     * sync-disconnected banner renders inside the view below, and the band drops
     * clear of it while it is showing. */
    <div className={cn(DESK_VIEW_GROUP_CLASSNAME, 'relative size-full')}>
      <Suspense
        fallback={
          <>
            <Loader />
            {/* List view renders the switcher itself, so until its chunk
             * arrives there would be nothing to switch with — a cold load of a
             * desk last left in list view would strand you on the loader with
             * no way back to the canvas. Float one for that window; it is the
             * same 60px box at the same 16px inset the header gives it, so
             * nothing moves when the real one takes over. */}
            {view === 'list' && <DeskViewToggle deskId={deskId} view={view} />}
          </>
        }
      >
        {view === 'list' ? <DeskListView deskId={deskId} /> : <DeskPageImpl />}
      </Suspense>
      {/* Canvas only. The canvas has no header row, so the switcher has to be
       * floated into its top-left — the one corner it doesn't already own
       * (add-note and options top-right, zoom controls bottom-left, minimap
       * bottom-right). List view renders the same switcher as an ordinary child
       * of its pane header instead, so that row places it by its own gutter and
       * gap; the two placements resolve to the same position, so it still
       * doesn't move when you switch. */}
      {view === 'canvas' && <DeskViewToggle deskId={deskId} view={view} />}
    </div>
  )
}
